import React, { useState } from 'react';

import { QueryResult, StepProps, track, useAutoNavigate, useMutation } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import {
  Button,
  CenteredWorkflowLayout,
  Flash,
  Flex,
  GoalIcon,
  Heading,
  Modal,
  Text,
} from '@betterment/style-closet-react';
import { formatDistanceStrict, parseISO } from 'date-fns';
import { useResolvedPath } from 'react-router-dom';

import { InvitationAcceptanceValues } from './Controller';

import { useCustomerResponse } from '../Checking/components/CustomerResponseProvider';
import { CustomerStatus, Invitation, InvitationStatus } from '../Checking/data';
import Footer from '../Shared/components/Footer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

interface InviteExpirationTextProps {
  invitation: Invitation;
}

const InviteExpirationText: React.FC<React.PropsWithChildren<InviteExpirationTextProps>> = ({ invitation }) => {
  const timeLeft = formatDistanceStrict(parseISO(invitation.expiresAt), Date.now(), {
    unit: 'day',
  });
  let expirationText = timeLeft;
  let isExpirationTextRed = false;

  if (timeLeft === '1 day') {
    isExpirationTextRed = true;
    expirationText = 'until tomorrow';
  } else if (timeLeft === '0 days') {
    isExpirationTextRed = true;
    expirationText = 'until 11:59pm (ET)';
  }
  return <Text color={isExpirationTextRed ? 'bmtOrange90' : 'inherit'}>{expirationText}</Text>;
};

interface Props extends StepProps<InvitationAcceptanceValues> {
  reloadInvitation: QueryResult<Invitation>['refetch'];
}

const AcceptInvitation: React.FC<React.PropsWithChildren<Props>> = ({ goToNextStep, flowState, reloadInvitation }) => {
  const { invitation } = flowState.values;
  const { pathname: basePath } = useResolvedPath('.');
  const navigateRouter = useAutoNavigate();
  const { customer, loading: isCustomerLoading, refetch: reloadCustomer } = useCustomerResponse();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isVisible, setVisible] = useState(false);
  const [shouldShowDeclineConfirmationModal, setDeclineConfirmationModal] = useState(false);
  const [makeAcceptRequest, { errors: acceptErrors }] = useMutation<{ accountId: number; status: string }>(
    urlBuilder.createEditAccessRequest(invitation.invitationId)
  );
  const [makeDeclineRequest, { errors: declineErrors }] = useMutation<{ status: string }>(
    urlBuilder.createDeclineInvitationRequest(invitation.invitationId)
  );

  if (isCustomerLoading) {
    return <FullPageLoader />;
  }

  const isCustomerDenied = customer?.customerStatus === CustomerStatus.Denied;

  const onAccept = async () => {
    if (!customer || isCustomerDenied) {
      void navigateRouter(
        urlBuilder.appCheckingSignups.absolute({
          return_to: basePath,
          account_type: 'joint',
          invitation_id: invitation.invitationId,
        })
      );
      return;
    }

    setSubmitting(true);
    setVisible(true);
    const response = await makeAcceptRequest();

    if (response.errors) {
      track('ElementViewed', { name: 'CoreproDown', flow: 'OpenJointCheckingSecondary' });
    }

    if (response.data) {
      track('ElementClicked', { name: 'AcceptJCInvite', flow: 'OpenJointCheckingSecondary' });
      if (response.data.status === InvitationStatus.Complete) {
        void reloadCustomer();
        goToNextStep();
      } else {
        void reloadInvitation();
      }
    } else {
      setSubmitting(false);
    }
  };

  const onDecline = async () => {
    setSubmitting(true);
    const response = await makeDeclineRequest();

    if (response.data) {
      track('ElementClicked', { name: 'DeclineJCInvite', flow: 'SecondaryDeclineJCInvite' });

      if (response.data.status === InvitationStatus.Declined) {
        locationAssign(urlBuilder.appHome.absolute());
      } else {
        void reloadInvitation();
      }
    } else {
      setSubmitting(false);
    }
  };

  const handleDeclineModalClose = () => setDeclineConfirmationModal(false);
  const onDeclineConfirm = () => {
    track('ElementClicked', { name: 'ConfirmDeclineJCInvite', flow: 'SecondaryDeclineJCInvite' });
    setDeclineConfirmationModal(true);
  };

  const senderFullName = `${invitation?.senderFirstName} ${invitation?.senderLastName}`;
  const shouldDisplayErrorFlash = (acceptErrors || declineErrors) && isVisible;
  const newCustomerMessage =
    "To finish signing up for Joint Checking, we'll need you to verify some personal information. It should take only a few minutes.";

  return (
    <CenteredWorkflowLayout title="Joint Checking signup">
      <CenteredWorkflowLayout.Slot name="content">
        {shouldDisplayErrorFlash && (
          <Box
            css={{
              marginBottom: 'space.400',
            }}
          >
            <Flash variant="error" onDismiss={() => setVisible(false)}>
              <Text color="bmtGrey90">Something went wrong. Please try again.</Text>
            </Flash>
          </Box>
        )}
        <Flex mb="8">
          <GoalIcon name="card" size={11} />
        </Flex>
        {shouldShowDeclineConfirmationModal && (
          <Modal
            pt="0"
            label="Confirm Decline Invitation Modal"
            contentWidth={['100%', '640px']}
            alignItems="center"
            closeHandler={handleDeclineModalClose}
          >
            <Modal.Slot name="heading">Are you sure you want to decline your invite?</Modal.Slot>
            <Modal.Slot name="content">
              <Text variant="copy">You will not be added to the Joint Checking account.</Text>
            </Modal.Slot>
            <Modal.Slot name="actions">
              <Button onClick={onDecline} disabled={isSubmitting}>
                Yes, decline
              </Button>
            </Modal.Slot>
          </Modal>
        )}
        <Heading as="h1" variant="primary">
          {senderFullName} invited you to a Joint Checking account
        </Heading>
        <Box
          css={{
            marginBottom: 'space.400',
          }}
        >
          {!customer && <Text as="p">{newCustomerMessage}</Text>}
          <Text as="p">
            You have <InviteExpirationText invitation={invitation} /> before this invite expires.
          </Text>
        </Box>
        <CenteredWorkflowLayout.Actions>
          <Button type="button" px="8" onClick={onAccept} disabled={isSubmitting}>
            Accept invite {(!customer || isCustomerDenied) && 'and continue'}
          </Button>
          <Button variant="tertiary" px="8" fontWeight="bolder" onClick={onDeclineConfirm}>
            Decline
          </Button>
        </CenteredWorkflowLayout.Actions>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};
export default AcceptInvitation;
